import { useEffect, useRef, useState, useCallback } from 'react';
import { inputEvidenceFresh } from '../../utils/screenActivity';
import { mergeDeviceSnapshot } from '../../utils/mergeDeviceSnapshot';

let nextRequest = 0;

export default function useAreaObservations(socket, areaId, devices, screens, enabled = true) {
  const [snapshot, setSnapshot] = useState({ key: '', liveStates: {}, tvInputReports: {} });
  const membership = devices.map((device) => Number(device.id)).sort((a, b) => a - b).join(',');
  const televisionIds = [...new Set(screens.filter((screen) => screen.displayKind !== 'unmanaged')
    .map((screen) => Number(screen.televisionDeviceId)).filter(Boolean))].sort((a, b) => a - b).join(',');
  const key = `${areaId}:${membership}`;
  const context = useRef(null);

  const requestTvState = useCallback((deviceId, requestedId, fresh = false) => {
    const current = context.current;
    const target = Number(deviceId);
    if (!current || current.key !== key || current.generation !== snapshot.generation
      || !current.ids.has(target) || socket?.connected === false) return null;
    const pending = current.pending.get(target);
    if (pending && Date.now() - pending.sentAt < 6000) return fresh ? null : pending.id;
    const id = requestedId || `area-input-${areaId}-${target}-${++nextRequest}`;
    current.pending.set(target, { id, sentAt: Date.now() });
    socket.emit('sendCommand', { deviceId: target, command: 'get_state', value: null, id });
    return id;
  }, [areaId, socket, key, snapshot.generation]);
  const isCurrentScope = useCallback(() => context.current?.generation === snapshot.generation
    && context.current?.key === key, [key, snapshot.generation]);

  useEffect(() => {
    if (!socket || !membership) return undefined;
    const current = { key, ids: new Set(membership.split(',').map(Number)), pending: new Map(), reports: {}, generation: ++nextRequest };
    context.current = current;
    setSnapshot({ key, generation: current.generation, liveStates: {}, tvInputReports: {} });
    const valid = (deviceId) => context.current === current && current.ids.has(Number(deviceId));
    const retain = (deviceId, report) => {
      if (!report || !valid(deviceId)) return;
      const previous = current.reports[deviceId];
      const previousTime = Number(previous?.collectedAt);
      const reportTime = Number(report.collectedAt);
      if (previousTime > 0 && previousTime <= Date.now() + 2000
        && (!Number.isFinite(reportTime) || reportTime < previousTime)) return;
      current.reports[deviceId] = previousTime === reportTime && previous?.receivedAt
        ? { ...report, receivedAt: previous.receivedAt } : report;
      setSnapshot((value) => ({ ...value, tvInputReports: { ...current.reports } }));
    };
    const update = ({ deviceId, state = {} }) => {
      if (!valid(deviceId)) return;
      retain(deviceId, state.tvInputManager);
      setSnapshot((value) => ({ ...value, liveStates: { ...value.liveStates,
        [deviceId]: { available: true, state: mergeDeviceSnapshot(value.liveStates[deviceId]?.state, state),
          observedAt: new Date().toISOString() } } }));
    };
    const unavailable = ({ deviceId, state }) => {
      if (!valid(deviceId)) return;
      retain(deviceId, state?.tvInputManager);
      setSnapshot((value) => ({ ...value, liveStates: { ...value.liveStates,
        [deviceId]: { ...value.liveStates[deviceId], state: mergeDeviceSnapshot(value.liveStates[deviceId]?.state, state, false), available: false,
          observedAt: new Date().toISOString() } } }));
    };
    const response = (payload = {}) => {
      const deviceId = Number(payload.deviceId);
      if (!valid(deviceId)) return;
      if (payload.type === 'tv_input_manager_update') retain(deviceId, payload.tvInputManager);
      if (payload.type !== 'state_response') return;
      if (current.pending.get(deviceId)?.id !== payload.requestId) return;
      current.pending.delete(deviceId);
      retain(deviceId, payload.state?.tvInputManager);
    };
    const failed = ({ deviceId, requestId }) => {
      if (valid(deviceId) && current.pending.get(Number(deviceId))?.id === requestId) {
        current.pending.delete(Number(deviceId));
      }
    };
    const reset = () => {
      current.pending.clear();
      current.reports = {};
      current.generation = ++nextRequest;
      setSnapshot({ key, generation: current.generation, liveStates: {}, tvInputReports: {} });
    };
    const connected = () => {
      reset();
      refreshChannels();
    };
    // This endpoint reads server socket maps only; it sends no device commands.
    const refreshChannels = () => {
      if (socket.connected === false || document.visibilityState === 'hidden') return;
      current.ids.forEach((deviceId) => socket.emit('requestDeviceState', { deviceId }));
    };
    const channelTimer = window.setInterval(refreshChannels, 15000);
    document.addEventListener('visibilitychange', refreshChannels);
    socket.on('deviceStateUpdate', update);
    socket.on('deviceStateUnavailable', unavailable);
    socket.on('deviceCommandResponse', response);
    socket.on('deviceNotConnected', failed);
    socket.on('disconnect', reset);
    socket.on('connect', connected);
    connected();
    return () => {
      if (context.current === current) context.current = null;
      window.clearInterval(channelTimer);
      document.removeEventListener('visibilitychange', refreshChannels);
      socket.off('deviceStateUpdate', update);
      socket.off('deviceStateUnavailable', unavailable);
      socket.off('deviceCommandResponse', response);
      socket.off('deviceNotConnected', failed);
      socket.off('disconnect', reset);
      socket.off('connect', connected);
    };
  }, [socket, key, membership]);

  useEffect(() => {
    if (!socket || !enabled || !televisionIds) return undefined;
    const refresh = (missingOnly = false) => {
      if (document.visibilityState === 'hidden') return;
      televisionIds.split(',').map(Number).forEach((deviceId) => {
        if (!missingOnly || !inputEvidenceFresh(context.current?.reports[deviceId])) requestTvState(deviceId);
      });
    };
    // Seed from the cached state first; each television can then finish independently.
    const initial = window.setTimeout(() => refresh(true), 0);
    const timer = window.setInterval(() => refresh(), 15000);
    const visible = () => refresh(true);
    socket.on('connect', visible);
    document.addEventListener('visibilitychange', visible);
    return () => {
      window.clearTimeout(initial);
      window.clearInterval(timer);
      socket.off('connect', visible);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [socket, enabled, key, televisionIds, requestTvState]);

  return { liveStates: snapshot.key === key ? snapshot.liveStates : {},
    tvInputReports: snapshot.key === key ? snapshot.tvInputReports : {}, requestTvState,
    scopeId: `${key}:${snapshot.generation}`, isCurrentScope };
}
